import auth0 from 'auth0-js';
import * as React from 'react';
import Cookies from 'universal-cookie';
import { CookieNames } from './constants';
const jwtDecode = require('jwt-decode');

const promisifiy = (thisArg, func) => {
  return (...args): any => {
    return new Promise((resolve, reject) => {
      func.apply(
        thisArg,
        args.concat((err, result) => {
          if (err) {
            return reject(err);
          }

          resolve(result);
        })
      );
    });
  };
};

const scope = [
  'read:operator-users',
  'read:venue-dashboard-stats',
  'read:venue-demographics',
  'read:menu-items',
  'create:menu-item',
  'update:menu-item',
  'create:menu',
  'read:menus',
  'update:menu',
  'delete:menu',
  'copy:menu',
  'read:disbursement-reports',
  'read:session-reports',
  'read:venues',
  'read:venue',
  'update:venue',
  'read:fulfillment-centers',
  'read:fulfillment-center',
  'create:fulfillment-center',
  'update:fulfillment-center',
  'delete:fulfillment-center',
  'delete:menu-item',
  'read:server',
  'create:server',
  'update:server',
  'read:servers',
  'openid',
  'profile',
].join(' ');

export class MFAAuthError extends Error {
  public mfaToken: string;
  public oobCode: string;
  public isMFAError = true;

  constructor(mfaToken: string, oobCode: string) {
    super('MFA Required');
    this.mfaToken = mfaToken;
    this.oobCode = oobCode;
  }
}

export type AuthTokenResponse = { accessToken: string; idToken: string };

export class AuthService {
  public cookies: Cookies;
  public auth0: any;
  public clientId: string;

  constructor(cookies?) {
    this.cookies = new Cookies(cookies);

    this.clientId = 'RN5zpiShUxeiQ3NzKvptczHKLmSQCeSN';

    this.auth0 = new auth0.WebAuth({
      clientID: this.clientId,
      domain: 'getnoble.auth0.com',
      responseType: 'token id_token',
      redirectUri: 'http://localhost:3000/login',
    });
  }

  login(username, password) {
    return new Promise<AuthTokenResponse>((resolve, reject) => {
      this.auth0.client.login(
        {
          connection: 'Username-Password-Authentication',
          username,
          password,
          scope,
          realm: 'Username-Password-Authentication',
          audience: 'backend-api',
        },
        (err, authResult) => {
          if (err) {
            if (err.code === 'mfa_required') {
              const mfaToken = err.original.response.body.mfa_token;
              this.sendChallenge(mfaToken)
                .then((json) => {
                  const error = new MFAAuthError(mfaToken, json.oob_code);
                  reject(error);
                })
                .catch(reject);
              return;
            }

            return reject(err);
          }

          const { accessToken, idToken } = authResult;
          resolve({ accessToken, idToken });
        }
      );
    });
  }

  async sendChallenge(mfaToken: string) {
    const response = await fetch('https://getnoble.auth0.com/mfa/challenge', {
      headers: {
        'Content-Type': 'application/json',
      },
      method: 'POST',
      body: JSON.stringify({
        client_id: this.clientId,
        challenge_type: 'otp oob',
        mfa_token: mfaToken,
      }),
    });

    return response.json();
  }

  public async loginWithMFACode(mfaToken: string, code: string, oobCode: string): Promise<AuthTokenResponse> {
    const response = await fetch('https://getnoble.auth0.com/oauth/token', {
      headers: {
        'Content-Type': 'application/json',
      },
      method: 'POST',
      body: JSON.stringify({
        client_id: this.clientId,
        mfa_token: mfaToken,
        oob_code: oobCode,
        binding_code: code,
        grant_type: 'http://auth0.com/oauth/grant-type/mfa-oob',
      }),
    });

    const json = await response.json();
    if (!response.ok) {
      throw new Error(JSON.stringify(json, null, 2));
    }

    const accessToken = json.access_token;
    const idToken = json.id_token;

    return { accessToken, idToken };
  }

  async resetPassword(email) {
    const resetFunc = promisifiy(this.auth0, this.auth0.changePassword);
    await resetFunc({
      connection: 'Username-Password-Authentication',
      email,
    });
  }

  async register(email, password, phone, firstName, lastName) {
    await this.registerPromisified(email, password, phone, firstName, lastName);
  }

  logout() {
    const options = {
      path: '/',
      domain: process.env.NODE_ENV === 'development' ? undefined : '.getnoble.co',
    };

    this.cookies.remove(CookieNames.accessToken, options);
    this.cookies.remove(CookieNames.idToken, options);

    this.cookies.remove(CookieNames.accessToken);
    this.cookies.remove(CookieNames.idToken);
  }

  decodeAccessToken(token: string) {
    try {
      const payload = jwtDecode(token);
      return payload;
    } catch {
      return null;
    }
  }

  setAccessToken(token: string) {
    const decoded = this.decodeAccessToken(token);
    (window as any).accessTokenInfo = decoded;
  }

  getAccessTokenInfo() {
    if (typeof window !== 'undefined') {
      return (window as any).accessTokenInfo;
    } else {
      const accessToken = this.cookies.get(CookieNames.accessToken);
      return this.decodeAccessToken(accessToken);
    }
  }

  get isLoggedIn() {
    const payload = this.getAccessTokenInfo();
    return payload ? Date.now() < payload.exp * 1000 : false;
  }

  getPermissions() {
    const payload = this.getAccessTokenInfo();
    return payload?.permissions ?? payload?.permissions?.split(' ') ?? [];
  }

  private registerPromisified(email, password, phone, firstName, lastName) {
    return new Promise((resolve, reject) => {
      this.auth0.signup(
        {
          connection: 'Username-Password-Authentication',
          email,
          username: email,
          password,
          scope,
          realm: 'Username-Password-Authentication',
          userMetadata: {
            phone_number: phone,
            first_name: firstName,
            last_name: lastName,
            name: [firstName, lastName].join(' '),
          },
        },
        (err, result) => {
          if (err) {
            return reject(err);
          }

          resolve(result);
        }
      );
    });
  }
}

export const AuthContext = React.createContext<AuthService | null>(null);
